import { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { Controller } from 'react-hook-form';
import {
    Stack, Typography, TextField, Button, IconButton, Paper, Tooltip,
    FormHelperText, FormControlLabel, FormControl, FormLabel, Switch, Radio, RadioGroup,
} from '@mui/material'
import { Add, HelpOutline } from "@mui/icons-material";
import ImageUploadSection from './ImageUploadSection';
import CategorySelect from './CategorySelect';
import CategoryAdd from './CategoryAdd';
import ProductVariantsTable from './ProductVariantsTable'
import ProductSpecsTable from './ProductSpecsTable';
import DuplicateList from './DuplicateList';
const ProductForm = ({
    hasLimit,
    dataset,
    imageFiles,
    setImageFiles,
    newImages,
    setNewImages,
    variants, setVariants,
    optionTypes, setOptionTypes,
    specs, setSpecs,
    selectedCategory,
    setSelectedCategory,
    register,
    errors,
    control,
    setValue,
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [taxRates, setTaxRates] = useState([]);
    const [open, setOpen] = useState(false);
    const [categories, setCategories] = useState([]);

    const loadCategories = async () => {
        try {
            const res = await axiosInstance.get('/category');

            const flattenCategories = (categories, parentId = null, level = 0) =>
                categories
                    .filter(c => c.parent_category_id === parentId)
                    .flatMap(c => [
                        { ...c, level },
                        ...flattenCategories(categories, c.category_id, level + 1)
                    ]);

            setCategories(flattenCategories(res.data));
        } catch (err) {
            console.error('カテゴリ取得失敗:', err);
            setCategories([]);
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        axiosInstance.get('/config/tax-rates')
            .then((res) => {
                if (!Array.isArray(res.data)) return;

                setTaxRates(res.data);

                if (dataset?.tax_rate !== undefined) {
                    setValue('tax_rate', dataset.tax_rate.toString());
                } else {
                    const defaultRate = res.data.find(rate => rate.is_default);
                    if (defaultRate) {
                        setValue('tax_rate', defaultRate.rate.toString());
                    }
                }
                // 他のフィールド
                setValue('status', dataset?.status ?? 'active');
                setValue('product_code', dataset?.product_code ?? '');
                setValue('product_name', dataset?.product_name ?? '');
                setValue('category_id', dataset?.category_id ?? '');
                setValue('product_description', dataset?.product_description ?? '');

                // variants 初期化
                if (dataset?.variants) {
                    // --- Table 表示用 state にセット ---
                    setVariants(dataset.variants);

                    // --- Controller にセット ---
                    dataset.variants.forEach((variant, index) => {
                        // 基本フィールド
                        setValue(`variants.${index}.sku`, variant.sku ?? '');
                        setValue(`variants.${index}.price`, variant.price ?? 0);
                        setValue(`variants.${index}.weight`, variant.weight ?? '');
                        setValue(`variants.${index}.status`, variant.status ?? 'active');
                        setValue(`variants.${index}.min_order_quantity`, variant.min_order_quantity ?? 1);
                        setValue(`variants.${index}.delivery_days`, variant.delivery_days ?? 0);
                        setValue(`variants.${index}.sort_order`, variant.sort_order ?? index);
                        setValue(`variants.${index}.jan_code`, variant.jan_code ?? '');
                        setValue(`variants.${index}.image_id`, variant.image_id ?? null);
                        setValue(`variants.${index}.imageFiles`, variant.imageFiles ?? []);

                        // --- option_combination のセット ---
                        Object.keys(variant.option_combination || {}).forEach(type => {
                            setValue(`variants.${index}.option_combination.${type}`, variant.option_combination[type] ?? '');
                        });

                        // --- inventory のセット ---
                        const inv = variant.inventory || {};
                        setValue(`variants.${index}.inventory.warehouse_id`, inv.warehouse_id ?? 0);
                        setValue(`variants.${index}.inventory.logical_stock`, inv.logical_stock ?? 0);
                        setValue(`variants.${index}.inventory.baseline_stock`, inv.baseline_stock ?? 0);
                        setValue(`variants.${index}.inventory.min_stock_level`, inv.min_stock_level ?? 0);
                    });
                }
            })
            .catch((err) => {
                console.error('税率の取得に失敗しました:', err);
            });
    }, [setValue, dataset]);

    return (
        <>
            <Paper variant='section'>
                <Typography component='h2' variant="h4">基本情報</Typography>
                <Stack spacing={2}>
                    <Controller
                        name="status"
                        control={control}
                        defaultValue="active"
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        {...field}
                                        checked={field.value === "active"}
                                        onChange={(e) =>
                                            field.onChange(e.target.checked ? "active" : "pending")
                                        }
                                        color="primary"
                                    />
                                }
                                label={field.value === "active" ? "販売中" : "非公開"}
                            />
                        )}
                    />
                    <Controller
                        name="product_code"
                        control={control}
                        defaultValue={""}
                        rules={{
                            required: '商品コードは必須です',
                            maxLength: { value: 50, message: '商品コードは50文字以内で入力してください' },
                        }}
                        render={({ field }) => (
                            <div style={{ position: "relative" }}>
                                <TextField
                                    {...field}
                                    label="商品コード"
                                    placeholder="商品コード"
                                    variant="outlined"
                                    error={!!errors.product_code}
                                    helperText={errors.product_code?.message}
                                    inputProps={{ maxLength: 50 }}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                />
                                <DuplicateList
                                    selfId={dataset?.product_id}
                                    value={field.value}
                                    displayPrimary="product_code"
                                    disabled={!isFocused}
                                    apiEndpoint="/product/check-product-code"
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="product_name"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: '商品名は必須です',
                            maxLength: { value: 255, message: '商品名は255文字以内で入力してください' }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="商品名"
                                placeholder="商品名"
                                variant="outlined"
                                error={!!errors.product_name}
                                helperText={errors.product_name?.message}
                                inputProps={{ maxLength: 255 }}
                                multiline minRows={1} maxRows={4}
                            />
                        )}
                    />
                    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                        <div>
                            <Controller
                                name="category_id"
                                control={control}
                                //rules={{ required: 'カテゴリーを選択してください' }}
                                defaultValue=""
                                render={({ field, fieldState: { error } }) => (
                                    <>
                                        <CategorySelect
                                            categories={categories}
                                            selectedCategory={field.value}
                                            setSelectedCategory={field.onChange}
                                            error={!!error}
                                        />
                                        {error && <FormHelperText error>{error.message}</FormHelperText>}
                                    </>
                                )}
                            />
                        </div>
                        <Button
                            startIcon={<Add />}
                            variant="outlined"
                            size="small"
                            onClick={() => setOpen(true)}
                        >
                            カテゴリー追加
                        </Button>
                    </Stack>
                    <CategoryAdd
                        open={open}
                        onClose={() => setOpen(false)}
                        categories={categories}
                        loadCategories={loadCategories}
                        setSelectedCategory={setSelectedCategory}
                    />
                    <FormControl component="fieldset" error={!!errors.tax_rate}>
                        <Typography variant='caption'>消費税率</Typography>
                        <Controller
                            name="tax_rate"
                            control={control}
                            rules={{ required: '税率を選択してください' }}
                            defaultValue=""
                            render={({ field }) => (
                                <RadioGroup row {...field}>
                                    {taxRates.map(rate => (
                                        <FormControlLabel
                                            key={rate.id}
                                            value={rate.rate.toString()}
                                            control={<Radio />}
                                            label={`${rate.name}（${rate.rate}%）`}
                                        />
                                    ))}
                                </RadioGroup>
                            )}
                        />
                        <FormHelperText>{errors.tax_rate?.message}</FormHelperText>
                    </FormControl>
                    <Controller
                        name="product_description"
                        control={control}
                        defaultValue=""
                        rules={{
                            //required: '商品説明は必須です',
                            validate: value =>
                                value.length <= 5000 || '商品説明は5000文字以内で入力してください'
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="商品概要及び特徴"
                                placeholder="商品説明"
                                multiline
                                minRows={8}
                                variant="outlined"
                                error={!!errors.product_description}
                                helperText={errors.product_description?.message}
                                inputProps={{ maxLength: 5000 }}
                            />
                        )}
                    />
                    {hasLimit('max_pdfs_per_product') && (
                        <Controller
                            name="catalog_pdf"
                            control={control}
                            defaultValue={null}
                            rules={{
                                validate: (file) => {
                                    if (!file) return true;
                                    if (file.type !== 'application/pdf') return 'PDFファイルのみアップロードできます';
                                    const maxSize = 10 * 1024 * 1024;
                                    if (file.size > maxSize) return '10MB以下のPDFにしてください';
                                    return true;
                                }
                            }}
                            render={({ field }) => (
                                <Stack direction="column" spacing={1} mb={2}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button variant="contained" component="label">
                                            PDF をアップロード
                                            <input
                                                type="file"
                                                accept="application/pdf"
                                                hidden
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    field.onChange(file);
                                                }}
                                            />
                                        </Button>

                                        {field.value && (
                                            <Typography variant="body2">
                                                選択中: {field.value.name}
                                            </Typography>
                                        )}
                                        {/* 保存済みPDFのリンク表示 */}
                                        {dataset?.catalog_pdf_url && (
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    const url = dataset.catalog_pdf_url
                                                        ? `${process.env.REACT_APP_IMAGE_BASE_URL}${dataset.catalog_pdf_url}`
                                                        : null;
                                                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                                }}
                                                disabled={!dataset.catalog_pdf_url}
                                            >
                                                アップロード済のカタログPDFを見る
                                            </Button>
                                        )}
                                    </Stack>
                                    {errors.catalog_pdf && (
                                        <FormHelperText error>
                                            {errors.catalog_pdf.message}
                                        </FormHelperText>
                                    )}
                                </Stack>
                            )}
                        />
                    )}
                </Stack>
            </Paper>
            {hasLimit('ec') && (
                <>
                    <Paper variant='section'>
                        <Typography component='h2' variant="h4">商品共通画像</Typography>
                        <ImageUploadSection
                            imageFiles={imageFiles}
                            setImageFiles={setImageFiles}
                            newImages={newImages}
                            setNewImages={setNewImages}
                        />
                    </Paper>
                    <Paper variant='section'>
                        <Stack direction='row' alignItems='center' mb={1}>
                            <Typography component='h2' variant="h4" sx={{ mb: 0 }}>スペック</Typography>
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        商品共通の情報を記載できます。<br />
                                        項目名に「材質」、内容に「ステンレス」などと入力できます。
                                    </Typography>
                                }
                                arrow
                                placement="right"
                            >
                                <IconButton size="small" aria-label="help">
                                    <HelpOutline fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack >
                        <ProductSpecsTable specs={specs} setSpecs={setSpecs} />
                    </Paper >
                </>
            )}
            <Paper variant='section'>
                <ProductVariantsTable
                    hasLimit={hasLimit}
                    variants={variants}
                    setVariants={setVariants}
                    control={control}
                    optionTypes={optionTypes}
                    setOptionTypes={setOptionTypes}
                />
            </Paper>
        </>
    );
};

export default ProductForm;
